'use client';

import { useTheme } from './ThemeProvider';
import { useEffect, useRef } from 'react';

const COLORS = {
  dark: {
    background: '#1a1a1a',
    gradients: [
      // Simplified gradients optimized for both mobile and desktop
      { 
        colors: ['#8B5CF6', '#3B82F6'], // Purple to Blue
        position: { x: 0.2, y: 0.2 }, 
        size: { mobile: 1.8, desktop: 1.4 }
      },
      { 
        colors: ['#10B981', '#3B82F6'], // Green to Blue
        position: { x: 0.8, y: 0.2 }, 
        size: { mobile: 1.8, desktop: 1.4 }
      },
      { 
        colors: ['#F59E0B', '#EC4899'], // Yellow to Pink
        position: { x: 0.2, y: 0.8 }, 
        size: { mobile: 1.8, desktop: 1.4 }
      },
      { 
        colors: ['#8B5CF6', '#EC4899'], // Purple to Pink
        position: { x: 0.8, y: 0.8 }, 
        size: { mobile: 1.8, desktop: 1.4 }
      }
    ],
    blur: { mobile: 100, desktop: 180 },
    opacity: { mobile: 0.5, desktop: 0.4 }
  },
  light: {
    background: '#f5f5f7',
    gradients: [
      { 
        colors: ['#C4B5FD', '#93C5FD'], // Light Purple to Light Blue
        position: { x: 0.2, y: 0.2 }, 
        size: { mobile: 1.8, desktop: 1.4 }
      },
      { 
        colors: ['#6EE7B7', '#93C5FD'], // Light Green to Light Blue
        position: { x: 0.8, y: 0.2 }, 
        size: { mobile: 1.8, desktop: 1.4 }
      },
      { 
        colors: ['#FDE68A', '#F9A8D4'], // Light Yellow to Light Pink
        position: { x: 0.2, y: 0.8 }, 
        size: { mobile: 1.8, desktop: 1.4 }
      },
      { 
        colors: ['#C4B5FD', '#F9A8D4'], // Light Purple to Light Pink
        position: { x: 0.8, y: 0.8 }, 
        size: { mobile: 1.8, desktop: 1.4 }
      }
    ],
    blur: { mobile: 80, desktop: 150 },
    opacity: { mobile: 0.4, desktop: 0.2 }
  }
};

export default function MinimalBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const { theme } = useTheme();
  const timeRef = useRef<number>(0);
  const blobPositionsRef = useRef<{ x: number; y: number; angle: number }[]>([]);
  const isMobileRef = useRef<boolean>(false);

  useEffect(() => {
    // Initialize blob positions
    blobPositionsRef.current = COLORS[theme].gradients.map((_, i) => ({
      x: window.innerWidth * Math.random(),
      y: window.innerHeight * Math.random(),
      angle: Math.random() * Math.PI * 2
    }));
  }, [theme]);

  useEffect(() => {
    const checkMobile = () => {
      isMobileRef.current = window.innerWidth < 768;
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    const drawGradientBlob = (
      x: number, 
      y: number, 
      colors: string[], 
      radius: number,
      opacity: number,
      size: number = 1
    ) => {
      const adjustedRadius = radius * size;
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, adjustedRadius);
      gradient.addColorStop(0, colors[0] + Math.floor(opacity * 255).toString(16).padStart(2, '0'));
      gradient.addColorStop(0.6, colors[1] + Math.floor(opacity * 0.7 * 255).toString(16).padStart(2, '0'));
      gradient.addColorStop(1, colors[1] + '00');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, adjustedRadius, 0, Math.PI * 2);
      ctx.fill();
    };

    const draw = (timestamp: number) => {
      const deltaTime = timestamp - (timeRef.current || timestamp);
      timeRef.current = timestamp;

      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      
      if (canvas.width !== rect.width * dpr || canvas.height !== rect.height * dpr) {
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
      }

      const colors = COLORS[theme];
      const isMobile = isMobileRef.current;
      
      // Fill background
      ctx.fillStyle = colors.background;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update and draw blobs
      blobPositionsRef.current.forEach((blob, i) => {
        const gradient = colors.gradients[i];
        const speed = 0.00005 * deltaTime;
        
        // Adjust movement range based on screen size
        const movementScale = isMobile ? 0.1 : 0.2;
        const radius = Math.min(canvas.width, canvas.height) * movementScale;
        
        const centerX = gradient.position.x * canvas.width;
        const centerY = gradient.position.y * canvas.height;

        blob.angle += speed;
        blob.x = centerX + Math.cos(blob.angle) * radius;
        blob.y = centerY + Math.sin(blob.angle) * radius;

        // Calculate size based on screen
        const baseSize = Math.min(canvas.width, canvas.height);
        const sizeMultiplier = isMobile ? gradient.size.mobile : gradient.size.desktop;
        const blobSize = baseSize * 0.6 * sizeMultiplier;

        // Get appropriate blur and opacity for device
        const blurAmount = isMobile ? colors.blur.mobile : colors.blur.desktop;
        const opacityAmount = isMobile ? colors.opacity.mobile : colors.opacity.desktop;

        drawGradientBlob(
          blob.x,
          blob.y,
          gradient.colors,
          blobSize,
          opacityAmount,
          1
        );
      });

      // Apply appropriate blur
      const blurAmount = isMobile ? colors.blur.mobile : colors.blur.desktop;
      ctx.filter = `blur(${blurAmount}px)`;
      ctx.drawImage(canvas, 0, 0);
      ctx.filter = 'none';

      animationRef.current = requestAnimationFrame(draw);
    };

    animationRef.current = requestAnimationFrame(draw);

    const handleResize = () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      animationRef.current = requestAnimationFrame(draw);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none w-screen h-screen"
      style={{
        zIndex: -1,
        backgroundColor: theme === 'dark' ? COLORS.dark.background : COLORS.light.background
      }}
    />
  );
}
